// src/config/dip.js — Configuration de la page Dip (formations)

// ─── FILTRES ──────────────────────────────────────────────────────────────────
// Portage de parent-coach-app/src/config/dip.ts
// Chaque dimension : { key, label, values, aliases?, labelMap?, collapsedCount? }

export const DIP_FILTERS = [
  {
    key: "type_formation",
    label: "Type de formation",
    collapsedCount: 10,
    values: [
      "BTS", "Licence", "Licence professionnelle", "Études de santé",
      "Certificat / Spécialisation", "CPGE / Prépa", "BUT", "École spécialisée",
      "École d'ingénieurs", "Arts, Design & Architecture", "Travail social",
      "Formations professionnelles", "École de commerce", "Sport & Animation",
      "Hôtellerie / Restauration", "DEUST", "LPE", "Sciences Po",
    ],
    aliases: {
      "BTS - BTSA - BTSM": "BTS",
      "brevet de technicien supérieur": "BTS",
      "brevet de technicien supérieur agricole": "BTS",
      "Licence": "Licence",
      "Diplômes d'université ou d'établissement": "Certificat / Spécialisation",
      "diplôme d'université": "Certificat / Spécialisation",
      "Licence professionnelle": "Licence professionnelle",
      "Etudes de santé": "Études de santé",
      "autre formation en santé": "Études de santé",
      "diplôme d'État du paramédical": "Études de santé",
      "Formations des écoles vétérinaires": "Études de santé",
      "Certificats de spécialisation et FCIL": "Certificat / Spécialisation",
      "certificat de spécialisation (ex MC)": "Certificat / Spécialisation",
      "CPGE": "CPGE / Prépa",
      "classe de mise à niveau post bac": "CPGE / Prépa",
      "Formations préparatoires à l'enseignement supérieur": "CPGE / Prépa",
      "BUT": "BUT",
      "formation d'école spécialisée": "École spécialisée",
      "Formations des écoles d'ingénieurs": "École d'ingénieurs",
      "C.M.I - Cursus Master en Ingénierie": "École d'ingénieurs",
      "Bachelor of Engineering": "École d'ingénieurs",
      "Bachelor of Science": "École d'ingénieurs",
      "Formations d'art, de design et du spectacle vivant": "Arts, Design & Architecture",
      "Formations d'architecture, du paysage et du patrimoine": "Arts, Design & Architecture",
      "Bachelor of Arts": "Arts, Design & Architecture",
      "Formations diplômantes du travail social": "Travail social",
      "diplôme d'État du travail social": "Travail social",
      "Formations professionnelles": "Formations professionnelles",
      "titre professionnel": "Formations professionnelles",
      "Formations des écoles de commerce et de management": "École de commerce",
      "I.A.E - Instituts d'administration des entreprises": "École de commerce",
      "Bachelor of Commerce": "École de commerce",
      "Formations aux métiers du sport": "Sport & Animation",
      "Formations aux métiers de l'hôtellerie restauration": "Hôtellerie / Restauration",
      "DCG": "Formations professionnelles",
      "DEUST": "DEUST",
      "LPE - Licence Professorat des Ecoles": "LPE",
      "Sciences Po - Instituts d'études politiques": "Sciences Po",
    },
  },
  {
    key: "school_status",
    label: "Statut",
    values: ["Public", "Privé"],
  },
  {
    key: "bac_type",
    label: "Type de bac",
    values: ["Général", "Technologique", "Professionnel"],
  },
  {
    key: "class_format",
    label: "Format",
    values: ["Présentiel", "Hybride", "Distanciel"],
    aliases: {
      "Temps plein en présentiel": "Présentiel",
      "En présentiel": "Présentiel",
      "Full-time": "Présentiel",
      "Initial": "Présentiel",
      "Voie scolaire ou apprentissage": "Hybride",
      "Classique et alternance": "Hybride",
      "Alternance / initiale": "Hybride",
      "Présentiel et distanciel": "Hybride",
      "Mix learning": "Hybride",
    },
  },
  {
    key: "selective_program",
    label: "Sélectivité",
    values: ["selective", "non_selective"],
    aliases: { "1": "selective" },
    labelMap: { selective: "Sélectif", non_selective: "Non sélectif" },
  },
  {
    key: "duration",
    label: "Durée",
    collapsedCount: 5,
    values: ["1 an", "2 ans", "3 ans", "4 ans", "5 ans", "1 à 2 ans", "1 à 3 ans", "3 à 5 ans"],
  },
  {
    key: "apprentissage",
    label: "Alternance",
    values: ["Obligatoire", "Non proposé"],
    aliases: {
      "Non": "Non proposé",
      "Non proposée": "Non proposé",
      "Oui": "Obligatoire",
      "Possible dès l'année 1": "Obligatoire",
      "Possible en année 2": "Obligatoire",
      "Possible en année 3": "Obligatoire",
    },
    labelMap: {
      "Obligatoire": "Alternance disponible",
      "Non proposé": "Non proposé",
    },
  },
];

// ─── SYSTEM PROMPT ────────────────────────────────────────────────────────────

export function buildDipSystemPrompt() {
  const filterDoc = DIP_FILTERS.map((f) => {
    const vals = f.values.map((v) => `"${v}"`).join(", ");
    return `  * ${f.key} (${f.label}) : ${vals}`;
  }).join("\n");

  const emptyFilterState = Object.fromEntries(DIP_FILTERS.map((f) => [f.key, []]));

  return `Tu es DIP, un assistant d'orientation scolaire. Tu transformes les demandes utilisateur en requêtes structurées pour rechercher des formations françaises.

Réponds UNIQUEMENT en JSON valide, sans markdown, sans texte avant ou après.

Format exact :
{
  "query": "mots clés full-text (titre, domaine, matières)",
  "filterState": ${JSON.stringify(emptyFilterState, null, 2)},
  "villes": [],
  "aroundRadius": 30000,
  "numericFilters": [],
  "summary": "1 phrase décrivant la recherche"
}

Règles :

─ query : RÈGLE CRITIQUE. Ne mets dans query QUE les termes qui ont une valeur sémantique propre au-delà des filtres.
  À ne JAMAIS mettre dans query : descripteurs de localisation ("à Paris", "proche de"), types de formation déjà dans filterState, descripteurs de coût/durée.
  À TOUJOURS garder dans query : domaines, filières, matières (informatique, médecine, design, sport...).

─ filterState : utilise UNIQUEMENT les valeurs listées. Laisser [] si non mentionné.
${filterDoc}
  * type_formation : N'appliquer QUE si le type est mentionné explicitement ("je veux un BTS"). Pour un domaine (cinéma, design), mettre dans query et laisser [].
  * selective_program : N'appliquer QUE si l'utilisateur mentionne EXPLICITEMENT "sélectif", "non sélectif", "sans sélection", "concours", "sur dossier". Des mots comme "accessible", "facile", "ouvert à tous" NE déclenchent PAS ce filtre.
  * apprentissage : "alternance", "apprentissage" → ["Obligatoire"]
  * class_format : format de cours uniquement. NE PAS confondre alternance et hybride.

─ villes : tableau de TOUTES les villes mentionnées, noms bruts (ex: ["Paris", "Lyon"]). [] si aucune.
─ aroundRadius (en mètres) :
  * ville(s) / "proche de" → 30000
  * département mentionné → 50000
  * région mentionnée → 100000
  * pas de ville → pas d'aroundRadius

─ numericFilters :
  * ["admission_rate > 0.3"] pour formations accessibles
  * ["fees_max < X"] si frais de scolarité inférieurs à X €
  * [] si aucun filtre numérique

─ summary : 1 phrase courte décrivant la recherche (inclure ville et type si présents)
─ Tenir compte de tout l'historique pour affiner les critères précédents`;
}

// ─── CONFIG ───────────────────────────────────────────────────────────────────

export const DIP_CONFIG = {
  algoliaIndex: "formations",
  geo: true,
  header: {
    icon: "D",
    title: "Dip",
    subtitle: "Expert formations",
  },
  filterDimensions: DIP_FILTERS,
  suggestions: [
    "Des BTS en informatique en alternance",
    "Une école d'ingénieurs accessible avec un Bac général",
    "Des formations en design sans sélection à Paris",
    "Une licence de psychologie en 3 ans",
  ],
  resultLabel: { singular: "formation", plural: "formations" },
};
